import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const SettingsContext = createContext({
  liveQuotePageEnabled: true,
  marketPageEnabled: true,
  signalPriceRefreshMinutes: 10,
  loading: true,
  refresh: () => {},
});

export function SettingsProvider({ children }) {
  const { user } = useAuth();
  const [liveQuotePageEnabled, setLiveQuotePageEnabled] = useState(true);
  const [marketPageEnabled, setMarketPageEnabled] = useState(true);
  const [signalPriceRefreshMinutes, setSignalPriceRefreshMinutes] = useState(10);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    api.get('/settings/public')
      .then((res) => {
        setLiveQuotePageEnabled(res.data?.liveQuotePageEnabled !== false);
        setMarketPageEnabled(res.data?.marketPageEnabled !== false);
        setSignalPriceRefreshMinutes(Number(res.data?.signalPriceRefreshMinutes) || 10);
      })
      .catch(() => {
        // If the check itself fails, don't block users from a working feature —
        // fail open on the frontend; the backend still enforces the real gate.
        setLiveQuotePageEnabled(true);
        setMarketPageEnabled(true);
      })
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isAdminOrSemi = user?.role === 'ROLE_ADMIN' || user?.role === 'ROLE_SEMI_ADMIN';

  return (
    <SettingsContext.Provider value={{
      // Admin/semi-admin are never gated in the UI — mirrors backend enforcement.
      liveQuotePageEnabled: isAdminOrSemi ? true : liveQuotePageEnabled,
      marketPageEnabled: isAdminOrSemi ? true : marketPageEnabled,
      signalPriceRefreshMinutes,
      loading,
      refresh,
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}
